// Mock fixtures so the dashboard renders without a backend.
//
// api.ts falls back to these whenever VITE_TELEMETRY_URL is unset / a
// placeholder, so the SPA is fully explorable offline during development and in
// screenshot/QA runs. The data is small but exercises every view (a GPS trace,
// several detectors, a play-through timeline, and a diagnostics run).

import type { DiagnosticRun, TripReport, TripSummary } from "./types.js";

const T0 = Date.parse("2026-07-10T08:00:00.000Z");
const min = (n: number) => T0 + n * 60_000;

// A short synthetic trace heading roughly north-east.
const trace: Array<{ lat: number; lng: number }> = Array.from({ length: 10 }, (_, i) => ({
  lat: 37.4219 + i * 0.0021,
  lng: -122.084 + i * 0.0017,
}));

export const mockTrips: TripSummary[] = [
  {
    id: "trip-2026-07-10-a",
    start: "2026-07-10T08:00:00.000Z",
    end: "2026-07-10T08:12:00.000Z",
    distanceM: 4200,
    discrepancyCount: 3,
  },
  {
    id: "trip-2026-07-09-b",
    start: "2026-07-09T17:30:00.000Z",
    end: "2026-07-09T17:41:00.000Z",
    distanceM: 3100,
    discrepancyCount: 1,
  },
  {
    id: "trip-2026-07-08-c",
    start: "2026-07-08T12:05:00.000Z",
    end: "2026-07-08T12:19:00.000Z",
    distanceM: 5600,
    discrepancyCount: 0,
  },
];

const reportA: TripReport = {
  trip: mockTrips[0],
  records: {
    guardEvaluations: trace.map((p, i) => ({
      tripId: mockTrips[0].id,
      ts: min(i),
      guard: i % 3 === 0 ? "advance" : "bearing",
      decision: i === 6 ? "reject" : "accept",
      lat: p.lat,
      lng: p.lng,
      offRouteM: i === 4 ? 72 : 4,
      polylineAgeMs: i === 7 ? 9200 : 1200,
      zoom: i === 2 ? 15 : 17,
      speedMps: i === 2 ? 6 : 12,
      bearingDeltaDeg: i === 6 ? 18 : 5,
      bearingGateDeg: 12,
      originMarkerId: i < 5 ? "origin-1" : "origin-2",
    })),
    stepAdvances: [
      {
        tripId: mockTrips[0].id,
        ts: min(3),
        fromStep: 0,
        toStep: 1,
        distanceToManeuverM: 58,
        displayDistanceM: 60,
        lat: trace[3].lat,
        lng: trace[3].lng,
      },
      {
        tripId: mockTrips[0].id,
        ts: min(8),
        fromStep: 1,
        toStep: 2,
        distanceToManeuverM: 12,
        displayDistanceM: 10,
        lat: trace[8].lat,
        lng: trace[8].lng,
      },
    ],
    reroutes: [
      {
        tripId: mockTrips[0].id,
        ts: min(5),
        reason: "off-route",
        offRouteM: 8,
        lat: trace[5].lat,
        lng: trace[5].lng,
      },
    ],
    ttsEvents: [
      {
        tripId: mockTrips[0].id,
        ts: min(2),
        text: "In 500 meters, turn right",
        phase: "prepare",
        distanceToManeuverM: 500,
        stepIndex: 1,
      },
      {
        tripId: mockTrips[0].id,
        ts: min(8),
        text: "Turn right now",
        phase: "execute",
        distanceToManeuverM: 160,
        stepIndex: 2,
      },
    ],
  },
  discrepancies: [
    {
      tripId: mockTrips[0].id,
      detector: "premature-advance",
      ts: min(3),
      lat: trace[3].lat,
      lng: trace[3].lng,
      severity: "warn",
      detail: { fromStep: 0, toStep: 1, distanceToManeuverM: 58 },
    },
    {
      tripId: mockTrips[0].id,
      detector: "false-reroute",
      ts: min(5),
      lat: trace[5].lat,
      lng: trace[5].lng,
      severity: "error",
      detail: { reason: "off-route", offRouteM: 8 },
    },
    {
      tripId: mockTrips[0].id,
      detector: "tts-out-of-sync",
      ts: min(8),
      lat: null,
      lng: null,
      severity: "warn",
      detail: { phase: "execute", distanceToManeuverM: 160, stepIndex: 2 },
    },
  ],
};

const reportB: TripReport = {
  trip: mockTrips[1],
  records: {
    guardEvaluations: trace.slice(0, 6).map((p, i) => ({
      tripId: mockTrips[1].id,
      ts: min(i),
      guard: "bearing",
      decision: "accept",
      lat: p.lat,
      lng: p.lng,
      offRouteM: 3,
      bearingDeltaDeg: 4,
      bearingGateDeg: 12,
    })),
    stepAdvances: [],
    reroutes: [],
    ttsEvents: [],
  },
  discrepancies: [
    {
      tripId: mockTrips[1].id,
      detector: "zoom-cascade-off",
      ts: min(2),
      lat: trace[2].lat,
      lng: trace[2].lng,
      severity: "info",
      detail: { zoom: 15, speedMps: 6, expectedZoom: 17 },
    },
  ],
};

const reportC: TripReport = {
  trip: mockTrips[2],
  records: { guardEvaluations: [], stepAdvances: [], reroutes: [], ttsEvents: [] },
  discrepancies: [],
};

export const mockReports: Record<string, TripReport> = {
  [reportA.trip.id]: reportA,
  [reportB.trip.id]: reportB,
  [reportC.trip.id]: reportC,
};

export const mockDiagnostics: DiagnosticRun[] = [
  {
    roundId: "aa-discoverability-2026-07-10",
    deviceId: "pixel-emu-01",
    capturedAt: "2026-07-10T09:15:00.000Z",
    manifest: {
      packageName: "dev.wakilabs.wakidrive",
      minCarApiLevel: 1,
      permissions: [
        "androidx.car.app.NAVIGATION_TEMPLATES",
        "androidx.car.app.ACCESS_SURFACE",
      ],
      categories: ["androidx.car.app.category.NAVIGATION"],
    },
    crossCheck: {
      ok: true,
      missing: [],
      note: "NAVIGATION_TEMPLATES + ACCESS_SURFACE present; projection root cause (v0.14.1) confirmed fixed.",
    },
  },
  {
    roundId: "aa-discoverability-2026-07-08",
    deviceId: "pixel-emu-01",
    capturedAt: "2026-07-08T14:02:00.000Z",
    manifest: {
      packageName: "dev.wakilabs.wakidrive",
      permissions: ["androidx.car.app.ACCESS_SURFACE"],
      categories: ["androidx.car.app.category.NAVIGATION"],
    },
    crossCheck: {
      ok: false,
      missing: ["androidx.car.app.NAVIGATION_TEMPLATES"],
      note: "Missing NAVIGATION_TEMPLATES -> app not discoverable in Android Auto launcher.",
    },
  },
];
